import { useEffect, useState } from "react";
import Drawer from "./Drawer";
import SearchableSelect from "./SearchableSelect";
import {
  createCalendarNote,
  updateCalendarNote,
  adminCreateCalendarNote,
  adminUpdateCalendarNote,
} from "../api/calendarNoteService";
import { showApiError, showToast } from "../utils/toast";

// Create / edit a calendar note spanning a date range. In admin mode the note
// is attributed to a chosen employee (employees prop required).
export default function CalendarNoteEditorDrawer({
  open, note, defaultStart, defaultEnd, admin = false, employees = [], onClose, onSaved,
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ title: "", description: "", start_date: today, end_date: today });
  const [employeeId, setEmployeeId] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const isEdit = !!note;

  useEffect(() => {
    if (!open) return;
    if (note) {
      setForm({
        title: note.title || "",
        description: note.description || "",
        start_date: (note.start_date || today).slice(0, 10),
        end_date: (note.end_date || today).slice(0, 10),
      });
      setEmployeeId(note.employee_id ? String(note.employee_id) : "");
    } else {
      setForm({
        title: "",
        description: "",
        start_date: defaultStart || today,
        end_date: defaultEnd || defaultStart || today,
      });
      setEmployeeId("");
    }
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, note, defaultStart, defaultEnd]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const valid = form.title.trim() && form.start_date && form.end_date
    && form.start_date <= form.end_date && (!admin || employeeId);

  const submit = async () => {
    setErrors({});
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        start_date: form.start_date,
        end_date: form.end_date,
      };
      if (admin) payload.employee_id = Number(employeeId);

      if (isEdit) {
        await (admin ? adminUpdateCalendarNote(note.id, payload) : updateCalendarNote(note.id, payload));
        showToast("success", "Note updated");
      } else {
        await (admin ? adminCreateCalendarNote(payload) : createCalendarNote(payload));
        showToast("success", "Note added");
      }
      onSaved?.();
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit note" : "New note"}
      subtitle="Pin a note across a date range on your calendar."
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || !valid}>
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add note"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        {admin && (
          <div className="field">
            <label className="field-label">Employee</label>
            <SearchableSelect
              value={employeeId}
              onChange={(v) => setEmployeeId(v ?? "")}
              options={employees.map((emp) => ({
                id: emp.id,
                label: emp.name,
                hint: emp.department?.name,
              }))}
              placeholder="Select employee…"
              searchPlaceholder="Search employees…"
              width="100%"
            />
            {errors.employee_id?.[0] && <div className="field-error">{errors.employee_id[0]}</div>}
          </div>
        )}
        <div className="row gap-md" style={{ flexWrap: "wrap" }}>
          <div className="field" style={{ flex: 1, minWidth: 150 }}>
            <label className="field-label">From</label>
            <input type="date" className="input" value={form.start_date} onChange={(e) => set({ start_date: e.target.value })} />
            {errors.start_date?.[0] && <div className="field-error">{errors.start_date[0]}</div>}
          </div>
          <div className="field" style={{ flex: 1, minWidth: 150 }}>
            <label className="field-label">To</label>
            <input
              type="date"
              className="input"
              value={form.end_date}
              min={form.start_date || undefined}
              onChange={(e) => set({ end_date: e.target.value })}
            />
            {errors.end_date?.[0] && <div className="field-error">{errors.end_date[0]}</div>}
          </div>
        </div>
        <div className="field">
          <label className="field-label">Title</label>
          <input className="input" value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="e.g. On leave · Audit week" />
          {errors.title?.[0] && <div className="field-error">{errors.title[0]}</div>}
        </div>
        <div className="field">
          <label className="field-label">Description <span className="t-muted text-xs">(optional)</span></label>
          <textarea className="textarea" rows={4} value={form.description} onChange={(e) => set({ description: e.target.value })} />
        </div>
      </div>
    </Drawer>
  );
}
